import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Plus } from "lucide-react";
import { EditorialPage, handwritten } from "../components/editorial";
import { faqLd, serializeLd } from "../lib/jsonLd";
import Button from "../components/Button";
import Navbar from "../components/Navbar";

// Dedicated support page. Referenced as the App Store / Play Store "Support URL"
// (letsloop.app/support). Apple Guideline 1.5 requires a functional page with a
// real contact method — the mailto below is that method; the FAQ is supporting context.

// aText mirrors the rendered answer as plain text for the page's FAQPage
// JSON-LD (schema must match visible copy; see lib/jsonLd.ts faqLd). Keep
// the two in sync when editing an answer.
type Faq = { q: string; a: React.ReactNode; aText: string };

const faqs: Faq[] = [
  {
    q: "How do I get help or contact LetsLoop?",
    aText:
      "Email hello@letsloop.app and a real person on our team will get back to you within 24-48 hours. Tell us what's happening (and, for a bug, your phone model and iOS/Android version) so we can help faster.",
    a: (
      <>
        Email{" "}
        <a href="mailto:hello@letsloop.app" className="faq-link">
          hello@letsloop.app
        </a>{" "}
        and a real person on our team will get back to you within 24&ndash;48
        hours. Tell us what&rsquo;s happening (and, for a bug, your phone model
        and iOS/Android version) so we can help faster.
      </>
    ),
  },
  {
    q: "Is LetsLoop free?",
    aText:
      "Yes, completely free. LetsLoop is user-led: real people host and join spontaneous local meet-ups (“loops”). No tickets, no paywalls, no pay-to-belong.",
    a: (
      <>
        Yes, completely free. LetsLoop is user-led: real people host and join
        spontaneous local meet-ups (&ldquo;loops&rdquo;). No tickets, no
        paywalls, no pay-to-belong.
      </>
    ),
  },
  {
    q: "Where does LetsLoop work?",
    aText:
      "We're building city by city, starting in London and across the UK. If it's quiet in your area you can still create a loop and help it grow, and we'd love to hear where you want us next: hello@letsloop.app.",
    a: (
      <>
        We&rsquo;re building city by city, starting in London and across the UK.
        If it&rsquo;s quiet in your area you can still create a loop and help it
        grow, and we&rsquo;d love to hear where you want us next:{" "}
        <a href="mailto:hello@letsloop.app" className="faq-link">
          hello@letsloop.app
        </a>
        .
      </>
    ),
  },
  {
    q: "How do I report someone, or something that feels off?",
    aText:
      "In the app, one tap lets you mute, block, or report any person, from their profile or any loop. Reports go to a human on our team and are read within 24 hours. You can also email safety@letsloop.app. We err on the side of users, not optics.",
    a: (
      <>
        In the app, one tap lets you <strong>mute, block, or report</strong> any
        person, from their profile or any loop. Reports go to a human on our
        team and are read within 24 hours. You can also email{" "}
        <a href="mailto:safety@letsloop.app" className="faq-link">
          safety@letsloop.app
        </a>
        . We err on the side of users, not optics.
      </>
    ),
  },
  {
    q: "How do I delete my account?",
    aText:
      "In the app: open You > Settings > Account > Delete account. This permanently removes your profile and personal data. Prefer we do it for you? Email hello@letsloop.app from your account's email address and we'll take care of it.",
    a: (
      <>
        In the app: open{" "}
        <strong>
          You &rarr; Settings &rarr; Account &rarr; Delete account
        </strong>
        . This permanently removes your profile and personal data. Prefer we do
        it for you? Email{" "}
        <a href="mailto:hello@letsloop.app" className="faq-link">
          hello@letsloop.app
        </a>{" "}
        from your account&rsquo;s email address and we&rsquo;ll take care of it.
      </>
    ),
  },
  {
    q: "Is LetsLoop safe?",
    aText:
      "Safety is built in from the first screen: real names and photos (no anonymous handles), visible host history, privacy zones that hide your exact home/work, per-loop join filters, and one-tap mute / block / report. More on our Safety page at letsloop.app/safety.",
    a: (
      <>
        Safety is built in from the first screen: real names and photos (no
        anonymous handles), visible host history, privacy zones that hide your
        exact home/work, per-loop join filters, and one-tap mute / block /
        report. More on our{" "}
        <Link to="/safety" className="faq-link">
          Safety page
        </Link>
        .
      </>
    ),
  },
  {
    q: "I'm having a technical problem",
    aText:
      "Sorry about that. Email hello@letsloop.app with a short description, your device model, and your app version (shown at the bottom of You > Settings), plus a screenshot if you can. We'll get you sorted.",
    a: (
      <>
        Sorry about that. Email{" "}
        <a href="mailto:hello@letsloop.app" className="faq-link">
          hello@letsloop.app
        </a>{" "}
        with a short description, your device model, and your app version (shown
        at the bottom of <strong>You &rarr; Settings</strong>), plus a
        screenshot if you can. We&rsquo;ll get you sorted.
      </>
    ),
  },
];

function FaqItem({ item, defaultOpen }: { item: Faq; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-black/10 shadow-[0_10px_30px_-24px_rgba(45,56,73,0.4)] overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-serif font-bold text-ink text-lg">{item.q}</span>
        <Plus
          size={22}
          className={`text-salmon flex-none transition-transform duration-200 ${
            open ? "rotate-45" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-6 pb-6 text-gray-700 text-base leading-relaxed max-w-[64ch]">
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  useEffect(() => {
    document.title = "Support: accounts, safety and your data | LetsLoop";
  }, []);

  return (
    <>
      <Navbar />
      <EditorialPage>
        {/* This page's OWN FAQ as FAQPage schema (prerendered into static
            HTML; mirrors the visible copy via aText). */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeLd(faqLd(faqs)) }}
        />
        <style>{`.faq-link{color:#FFA07A;font-weight:600;border-bottom:2px solid rgba(255,160,122,.4);}
        .faq-link:hover{border-color:#FFA07A;}`}</style>

        {/* ── Header ────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 pt-32 pb-16 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl text-salmon mb-3 -rotate-1 origin-left"
            style={handwritten}
          >
            support
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-ink tracking-tight leading-[0.95] text-5xl md:text-7xl mb-6"
          >
            Need a hand? We&rsquo;re{" "}
            <span className="italic text-salmon">right here.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-lg md:text-2xl text-gray-700 max-w-2xl"
          >
            Questions, account help, or something that doesn&rsquo;t feel right:
            reach a real human on the LetsLoop team. No bots, no ticket maze.
          </motion.p>
        </section>

        {/* ── Primary contact (the required contact method) ─────── */}
        <section className="px-6 md:px-12 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl shadow-[0_18px_50px_-28px_rgba(45,56,73,0.45)] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          >
            <div>
              <h2 className="font-serif font-bold text-ink text-2xl mb-2">
                Email us
              </h2>
              <p className="text-gray-700">
                The fastest way to reach us. We read every message and reply
                within <strong>24&ndash;48 hours</strong>.
              </p>
            </div>
            <a href="mailto:hello@letsloop.app" className="flex-none">
              <Button variant="primary" size="lg">
                <Mail size={18} className="mr-2 inline" />
                hello@letsloop.app
              </Button>
            </a>
          </motion.div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 max-w-4xl mx-auto pt-16">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-salmon mb-5">
            Common questions
          </p>
          <div className="grid gap-4">
            {faqs.map((f, i) => (
              // Key the wrapper, not FaqItem: this repo has no @types/react, so
              // TS has no JSX.LibraryManagedAttributes and rejects `key` on a
              // local function component. Same pattern as EventsPage/EventCard.
              <div key={f.q}>
                <FaqItem item={f} defaultOpen={i === 0} />
              </div>
            ))}
          </div>
        </section>

        {/* ── More links ────────────────────────────────────────── */}
        <section className="px-6 md:px-12 max-w-4xl mx-auto pt-12">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-salmon mb-5">
            More
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { to: "/safety", label: "Safety" },
              { to: "/privacy", label: "Privacy Policy" },
              { to: "/terms", label: "Terms of Service" },
              { to: "/how-it-works", label: "How it works" },
              { to: "/download", label: "Get the app" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex items-center gap-2 bg-white border border-black/10 rounded-full px-4 py-2.5 text-ink font-semibold text-sm hover:border-salmon transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </section>

        {/* ── Footer CTA ────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-3xl text-salmon mb-3" style={handwritten}>
              keep us honest
            </p>
            <h3 className="text-3xl md:text-5xl font-serif tracking-tight mb-6 leading-[1.05]">
              Something feels off?{" "}
              <span className="italic text-salmon">Tell us.</span>
            </h3>
            <p className="text-lg text-gray-700 mb-8 max-w-xl mx-auto leading-relaxed">
              Every report is read by a human within 24 hours. We err on the
              side of users, not optics. Mail{" "}
              <a
                href="mailto:safety@letsloop.app"
                className="text-salmon font-semibold border-b-2 border-salmon/40 hover:border-salmon"
              >
                safety@letsloop.app
              </a>{" "}
              and we&rsquo;ll respond.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/how-it-works">
                <Button variant="primary" size="lg">
                  See how it works
                  <ArrowRight size={16} className="ml-2 inline" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </section>
      </EditorialPage>
    </>
  );
}
